// Shared visual vocabulary for the projects list.
//
// Grid, Timeline and Table are three renderings of the SAME record, so every
// derived value they show — identity colour, monogram, status label, schedule
// state — is computed here once. If a project is teal and "6 days overdue" on a
// card, it must be teal and "6 days overdue" in the table and on the timeline.
//
// Nothing in here reads tracked hours. Time lives in the project's Analytics
// panel and in Work Summary; this page is about the project record itself.

import { isGeneralProject } from "./projectHelpers";

/* ── identity ──────────────────────────────────────────────────────────────
   A stable colour per project, so the grid is scannable by shape and hue
   rather than by reading every title. Seeded from the id (immutable) and not
   the name, so renaming a project doesn't recolour it.

   `t.series` is the CVD-validated categorical spread from tokens.js. Its
   documented caveat is that light-theme teal/yellow/pink fall under 3:1 on a
   white card, whose stated relief is a visible label beside the mark — every
   place we use this colour, the project name sits next to it. */
export function identityHue(t, project) {
  const seed = String(project?._id || project?.id || project?.name || "");
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) % 9973;
  return t.series[h % t.series.length];
}

// The tile glyph: the project's own code when it has one, otherwise initials.
export function monogramOf(project) {
  const code = (project?.code || "").trim();
  if (code) return code.slice(0, 4).toUpperCase();
  return String(project?.name || "?")
    .split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
}

/* ── status ────────────────────────────────────────────────────────────────
   The project's own `status` field. Deliberately NOT a computed "health":
   inferring that a project is in trouble is a different feature with a
   different burden of proof, and this page doesn't claim it. */
export const STATUS_META = {
  active:    { label: "Active",    key: "positive" },
  planning:  { label: "Planning",  key: "info" },
  on_hold:   { label: "On hold",   key: "warning" },
  completed: { label: "Completed", key: "info" },
  cancelled: { label: "Cancelled", key: "negative" },
  archived:  { label: "Archived",  key: "neutral" },
};
export const statusMeta = (s) => STATUS_META[s] || { label: s || "Unknown", key: "neutral" };
export const statusColor = (t, s) => t.status[statusMeta(s).key];

// Statuses that mean "no longer being worked on".
export const isClosed = (p) =>
  p?.status === "completed" || p?.status === "archived" || p?.status === "cancelled";

/* ── priority ──────────────────────────────────────────────────────────────
   On the model, but the create/edit form never writes it, so most projects
   carry the schema default of "medium". Only the two levels worth interrupting
   someone for get a chip; the rest render nothing rather than decorating every
   card with a value nobody set. */
export const PRIORITY_META = {
  urgent: { label: "Urgent", key: "negative" },
  high:   { label: "High",   key: "warning" },
};
export const priorityMeta = (p) => PRIORITY_META[p] || null;

/* ── type ─────────────────────────────────────────────────────────────────── */
export const TYPE_LABEL = {
  internal: "Internal",
  client: "Client work",
  maintenance: "Maintenance",
  research: "Research",
  training: "Training",
};
export const typeLabel = (p) => TYPE_LABEL[p?.type] || null;

/* ── client ────────────────────────────────────────────────────────────────
   `clientId` is populated with firstName/lastName/email — a person, not a
   company name — so build the label defensively and fall back to the email
   rather than rendering "undefined undefined". */
export function clientLabel(project) {
  const c = project?.clientId;
  if (!c || typeof c !== "object") return null;
  const name = `${c.firstName || ""} ${c.lastName || ""}`.trim();
  return name || c.email || null;
}

/* ── team ──────────────────────────────────────────────────────────────────
   teamMembers[].role (lead / developer / designer / tester / analyst /
   contributor) is stored today and was surfaced nowhere in the UI. Note it is
   NOT the same field as the populated user's `role` (org_admin / org_user) —
   they are siblings on the subdocument and mean different things. */
export function memberEntries(project) {
  return (project?.teamMembers || [])
    .filter((m) => m && m.isActive !== false)
    .map((m, i) => {
      const u = m.userId && typeof m.userId === "object" ? m.userId : null;
      const name = u ? `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Member" : "Member";
      return {
        key: String(u?._id || u?.id || m.userId || i),
        user: u,
        name,
        email: u?.email || "",
        projectRole: m.role || "contributor",   // membership role, not org role
      };
    });
}

/* ── schedule ──────────────────────────────────────────────────────────────
   Elapsed days against the start→end range.

   This is labelled SCHEDULE everywhere, never "progress". Days elapsed is not
   a measure of how much work is done, and a bar labelled "progress" would make
   that claim at a glance on every card in the grid.

   Returns a fully-resolved descriptor so no caller has to re-derive the wording
   or re-decide the colour. */
// Whole-day index for a date, in UTC — the clock the date-only project fields
// are written on. Two dates on the same calendar day always share an index, so
// day arithmetic can't drift with the time of day it happens to be run.
const dayIndex = (d) => Math.floor(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 86400000);

export function scheduleOf(project, now = new Date()) {
  if (isGeneralProject(project)) {
    return { kind: "system", note: "Always available — no run to plan", right: "System project" };
  }
  const rawStart = project?.startDate;
  const rawEnd = project?.endDate;
  const start = rawStart ? new Date(rawStart) : null;
  const end = rawEnd ? new Date(rawEnd) : null;
  const valid = (d) => d instanceof Date && !Number.isNaN(d.getTime());

  if (!valid(start) || !valid(end)) {
    return { kind: "none", note: "No dates set", right: null };
  }

  // Calendar days, not elapsed milliseconds.
  //
  // Project dates are date-only values stored at UTC midnight
  // (`new Date("2026-08-12").toISOString()`), so a straight
  // `Math.round((end - now) / DAY)` measures the distance from *this instant*
  // to midnight. A project due today then reads "0 days left" all morning and
  // flips to "1 day overdue" after noon — the deadline appears to pass while
  // nobody is looking at it. Flooring both sides to a UTC day index makes the
  // count what the label claims: whole days between two dates.
  const total = Math.max(1, dayIndex(end) - dayIndex(start));
  const elapsed = dayIndex(now) - dayIndex(start);
  const pct = Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)));
  const daysLeft = dayIndex(end) - dayIndex(now);
  const closed = isClosed(project);
  const overdue = !closed && daysLeft < 0;
  const soon = !closed && daysLeft >= 0 && daysLeft <= 14;

  return {
    kind: closed ? "closed" : overdue ? "overdue" : soon ? "soon" : "open",
    start, end, pct, daysLeft, overdue, closed, soon,
    // A finished project cannot be overdue — its run is simply over. Painting a
    // closed project red is the false alarm that teaches people to ignore red.
    right: closed
      ? "Finished"
      : overdue
        ? `${Math.abs(daysLeft)} ${Math.abs(daysLeft) === 1 ? "day" : "days"} overdue`
        : `${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`,
  };
}

// One place decides what colour a schedule is, so the bar, its caption and the
// table's due date can never disagree.
export function scheduleColor(t, sched) {
  if (!sched || sched.kind === "none" || sched.kind === "system") return t.c.textTertiary;
  if (sched.closed) return t.c.textTertiary;
  if (sched.overdue) return t.status.negative;
  if (sched.soon) return t.status.warning;
  return t.c.primary;
}

/* ── goals ─────────────────────────────────────────────────────────────────
   Reads the approved/pending split from /projects/stats-summary. Returns null
   when the stats row hasn't arrived or the project has none, so callers render
   a stated empty ("None set") instead of an empty bar. */
export function goalsOf(stat) {
  if (!stat) return null;
  const total = Number(stat.goals) || 0;
  if (total <= 0) return null;
  const approved = Math.min(total, Math.max(0, Number(stat.goalsApproved) || 0));
  return { total, approved, pending: total - approved };
}
